// Mock profile module (spec §13): profile hub data, photo, service requests,
// nominees (saved as a whole list — §17.12), settings, change mPIN/password
// (all rules incl. email-name and birth-year — §17.10, server references —
// §17.11) and de-register.
import type {
  Nominee,
  ProfileInfo,
  ProfileSettings,
  ServiceRequest,
  ServiceRequestKind,
} from '@/types/phase2.types';
import { loadDb, nextServiceRequestRef, saveDb } from '../db';
import { mockNow } from '../clock';
import { fail, mockId, pause, paymentReference } from '../util';
import { resendOtpSession, startOtpSession, verifyOtpSession } from './otp';

const SESSION_KEY = 'nova_phase2_session_active';

export interface ProfileSeed {
  name?: string;
  product?: string;
  applicationRef?: string;
  email?: string;
  yearOfBirth?: string;
}

/** Returns the profile hub data. The seed carries fields that belong to the
 *  real customer record (name, product…) so the mock can keep its own state
 *  (photo, last login, settings) around them. */
export const getProfile = async (seed: ProfileSeed = {}): Promise<ProfileInfo> => {
  await pause(300);
  const db = loadDb();
  // "Last login" is the PREVIOUS login, never the current one (§13.1). A new
  // browser session rolls the timestamps forward once.
  if (!sessionStorage.getItem(SESSION_KEY)) {
    sessionStorage.setItem(SESSION_KEY, '1');
    db.previousLoginAt = db.currentLoginAt;
    db.currentLoginAt = mockNow(db);
    saveDb(db);
  }
  return {
    name: seed.name ?? 'NOVA CUSTOMER',
    product: seed.product ?? 'Savings Account',
    applicationRef: seed.applicationRef ?? 'NB00000000',
    lastLoginAt: db.previousLoginAt,
    firstLogin: !db.previousLoginAt,
    photoDataUrl: db.photoDataUrl,
    kycState: db.kycState,
    mobileBanking: db.limits.mobileBanking,
    email: seed.email ?? 'customer@novabank.in',
    yearOfBirth: seed.yearOfBirth,
  };
};

export const setProfilePhoto = async (dataUrl: string | null): Promise<void> => {
  await pause(400);
  const db = loadDb();
  if (dataUrl) db.photoDataUrl = dataUrl;
  else delete db.photoDataUrl;
  saveDb(db);
};

// ---------- service requests (§13.5, §13.6) ----------

export const listServiceRequests = async (): Promise<ServiceRequest[]> => {
  await pause(350);
  return [...loadDb().serviceRequests];
};

export const createServiceRequest = async (input: {
  kind: ServiceRequestKind;
  category: string;
  description: string;
}): Promise<ServiceRequest> => {
  await pause(500);
  const db = loadDb();
  const request: ServiceRequest = {
    ref: nextServiceRequestRef(),
    kind: input.kind,
    category: input.category,
    description: input.description,
    status: 'OPEN',
    createdAt: mockNow(db),
  };
  db.serviceRequests.unshift(request);
  saveDb(db);
  return request;
};

// ---------- nominees (§13.6, §17.12) ----------

export const getNominees = async (): Promise<Nominee[]> => {
  await pause(300);
  return [...loadDb().nominees];
};

export const saveNominees = async (nominees: Nominee[]): Promise<Nominee[]> => {
  await pause(500);
  if (nominees.length > 4) fail('TOO_MANY', 'You can add up to 4 nominees.');
  const total = nominees.reduce((sum, n) => sum + (Number(n.share) || 0), 0);
  if (nominees.length > 0 && total !== 100) {
    fail(
      'SHARES_NOT_100',
      `Nominee shares must total 100%. Right now they total ${total}%. Adjust the shares and try again.`
    );
  }
  const db = loadDb();
  db.nominees = nominees.map((n) => ({ ...n, id: n.id || mockId('nom') }));
  saveDb(db);
  return db.nominees;
};

// ---------- settings (§13.7) ----------

export const getSettings = async (): Promise<ProfileSettings> => {
  await pause(200);
  return { ...loadDb().settings };
};

export const saveSettings = async (settings: ProfileSettings): Promise<void> => {
  await pause(300);
  const db = loadDb();
  db.settings = { ...settings };
  saveDb(db);
};

// ---------- change mPIN / password (§13.7, §17.10, §17.11) ----------

const EASY_MPINS = new Set(['123456', '654321']);

export const validateChangeMpin = async (currentMpin: string, newMpin: string): Promise<void> => {
  await pause(350);
  const db = loadDb();
  if (!/^\d{6}$/.test(newMpin)) fail('MPIN_FORMAT', 'mPIN must be 6 digits.');
  if (EASY_MPINS.has(newMpin)) fail('MPIN_EASY', 'That mPIN is too easy to guess.');
  if (/^(\d)\1{5}$/.test(newMpin)) fail('MPIN_REPEATED', 'mPIN cannot be the same digit repeated.');
  if (currentMpin !== db.limits.mpin) fail('MPIN_INCORRECT', 'The current mPIN is incorrect.');
};

export const validateChangePassword = async (
  currentPassword: string,
  newPassword: string,
  seed: ProfileSeed = {}
): Promise<void> => {
  await pause(350);
  const db = loadDb();
  if (newPassword.length < 8 || newPassword.length > 20) {
    fail('PWD_LENGTH', 'Password must be 8 to 20 characters.');
  }
  if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
    fail('PWD_COMPLEXITY', 'Use upper and lower case, a number and a special character.');
  }
  const emailName = (seed.email ?? '').split('@')[0];
  if (emailName && newPassword.toLowerCase().includes(emailName.toLowerCase())) {
    fail('PWD_EMAIL', 'Password must not contain your email name.');
  }
  if (seed.yearOfBirth && newPassword.includes(seed.yearOfBirth)) {
    fail('PWD_YOB', 'Password must not contain your year of birth.');
  }
  if (currentPassword !== db.limits.password) {
    fail('PWD_INCORRECT', 'The current password is incorrect.');
  }
};

export const requestProfileOtp = async (): Promise<{ authId: string; expiresInSeconds: number }> =>
  startOtpSession('PROFILE');

export const resendProfileOtp = async (authId: string): Promise<{ expiresInSeconds: number }> =>
  resendOtpSession(authId);

const nextChangeReference = (): string => {
  const db = loadDb();
  db.paymentCounter += 1;
  const ref = paymentReference(new Date(mockNow(db)), db.paymentCounter);
  saveDb(db);
  return ref;
};

export const verifyAndChangeMpin = async (
  authId: string,
  newMpin: string,
  otp: string
): Promise<{ reference: string }> => {
  await verifyOtpSession(authId, otp);
  const db = loadDb();
  db.limits.mpin = newMpin;
  if (db.limits.upiPin) db.limits.upiPin = newMpin;
  db.limits.mpinFailures = 0;
  db.limits.mpinLockedUntil = undefined;
  saveDb(db);
  return { reference: nextChangeReference() };
};

export const verifyAndChangePassword = async (
  authId: string,
  newPassword: string,
  otp: string
): Promise<{ reference: string }> => {
  await verifyOtpSession(authId, otp);
  const db = loadDb();
  db.limits.password = newPassword;
  saveDb(db);
  // The server would log every other session out here; the mock has no other
  // sessions, so the reference is all that comes back.
  return { reference: nextChangeReference() };
};

// ---------- de-register (§13.7) ----------

export const deregister = async (mpin: string): Promise<void> => {
  await pause(500);
  const db = loadDb();
  if (mpin !== db.limits.mpin) fail('MPIN_INCORRECT', 'The mPIN is incorrect.');
  db.limits.mobileBanking = false;
  saveDb(db);
  sessionStorage.removeItem(SESSION_KEY);
};
